<template>
  <div class="pricing-view-container">
    <div class="pricing-content">
      <div class="pricing-card" id="pricing">
        <div v-if="hasPricingData" class="pricing-list">
          <!-- Card Transaction -->
          <div v-if="cardPricing" class="pricing-item">
            <div class="pricing-label">
              {{ $t('pricing.cardTransaction') }}
            </div>
            <div class="pricing-value">
              <span class="pricing-amount">{{ cardPricingDisplayValue }}</span>
            </div>
          </div>

          <!-- Blended Card Transaction -->
          <div v-if="hasBlendedCardPricing" class="pricing-item">
            <div class="pricing-label">
              {{ $t('pricing.blendedCardTransaction') }}
            </div>
            <div class="pricing-value">
              <span
                v-for="(blendedFee, index) in blendedCardPricingDetails"
                :key="`profile-blended-card-fee-${index}`"
                class="pricing-amount s-block"
              >
                {{ blendedFee }}
              </span>
            </div>
          </div>

          <!-- ACH Transfer -->
          <div v-if="achPricing" class="pricing-item">
            <div class="pricing-label">
              {{ $t('pricing.achTransfer') }}
            </div>
            <div class="pricing-value">
              <span class="pricing-amount">{{ formatBasisPoints(achPricing.basis_points) }}</span>
              <span v-if="achPricing.min_fee || achPricing.max_fee" class="pricing-note">
                ({{ formatMinMax(achPricing.min_fee, achPricing.max_fee) }})
              </span>
            </div>
          </div>

          <!-- SEPA Transfer -->
          <div v-if="sepaPricing" class="pricing-item">
            <div class="pricing-label">
              {{ $t('pricing.sepaTransfer') }}
            </div>
            <div class="pricing-value">
              <span class="pricing-amount">{{ formatPricing(sepaPricing) }}</span>
              <span v-if="sepaPricing.max_fee" class="pricing-note">
                ({{ formatCurrency(sepaPricing.max_fee) }} maximum)
              </span>
            </div>
          </div>

          <!-- BACS Transfer -->
          <div v-if="bacsPricing" class="pricing-item">
            <div class="pricing-label">
              {{ $t('pricing.bacsTransfer') }}
            </div>
            <div class="pricing-value">
              <span class="pricing-amount">{{ formatPricing(bacsPricing) }}</span>
              <span v-if="bacsPricing.max_fee" class="pricing-note">
                ({{ formatCurrency(bacsPricing.max_fee) }} maximum)
              </span>
            </div>
          </div>
        </div>

        <!-- No pricing data -->
        <div v-else class="no-pricing">
          <c-inline-notification variant="info">
            {{ $t('pricing.noPricingData') }}
          </c-inline-notification>
        </div>
      </div>
    </div>

    <div class="pricing-sidebar">
      <TableOfContents :sections="sections" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import TableOfContents from '@/components/common/TableOfContents.vue';
import { CardPricingType } from '@/api/paymentApi';
import { getBlendedCardPricingDetails, hasBlendedCardPricing } from '@/app/utils/common/functions/cardPricingFormatter';

export default {
  name: 'ProfilePricing',
  components: {
    TableOfContents,
  },
  data() {
    return {
      sections: [
        { id: 'pricing', title: this.$t('pricing.title'), show: true },
      ],
    };
  },
  computed: {
    ...mapGetters('app', ['getCardPricing', 'getACHPricing', 'getBACSPricing', 'getSEPAPricing', 'getPaymentMethodConfig']),
    isCardEnabled() {
      const config = this.getPaymentMethodConfig;
      return !config?.CARD?.disabled;
    },
    isAchEnabled() {
      const config = this.getPaymentMethodConfig;
      return !config?.ACH_DEBIT?.disabled;
    },
    isSepaEnabled() {
      const config = this.getPaymentMethodConfig;
      return !config?.SEPA_DEBIT?.disabled;
    },
    isBacsEnabled() {
      const config = this.getPaymentMethodConfig;
      return !config?.BACS_DEBIT?.disabled;
    },
    cardPricing() {
      return this.isCardEnabled ? this.getCardPricing : null;
    },
    cardPricingTypeLabel() {
      if (!this.cardPricing?.pricing_type) {
        return null;
      }

      if (this.cardPricing.pricing_type === CardPricingType.ICPLUSPLUS) {
        return this.$t('pricing.icPlusPlus');
      }

      if (this.cardPricing.pricing_type === CardPricingType.BLENDEDPRICINGFLATRATE) {
        return this.$t('pricing.blendedPricingFlatRate');
      }

      return this.cardPricing.pricing_type;
    },
    cardPricingDisplayValue() {
      if (!this.cardPricing) {
        return null;
      }

      const formattedPricing = this.formatPricing(this.cardPricing);
      if (!this.cardPricingTypeLabel) {
        return formattedPricing;
      }

      if (this.cardPricing.pricing_type === CardPricingType.ICPLUSPLUS) {
        return `${this.cardPricingTypeLabel}: ${formattedPricing}`;
      }

      return `${this.cardPricingTypeLabel} (${formattedPricing})`;
    },
    blendedCardPricingDetails() {
      return getBlendedCardPricingDetails(this.cardPricing);
    },
    hasBlendedCardPricing() {
      return hasBlendedCardPricing(this.cardPricing);
    },
    achPricing() {
      return this.isAchEnabled ? this.getACHPricing : null;
    },
    sepaPricing() {
      return this.isSepaEnabled ? this.getSEPAPricing : null;
    },
    bacsPricing() {
      return this.isBacsEnabled ? this.getBACSPricing : null;
    },
    hasPricingData() {
      return this.cardPricing || this.achPricing || this.sepaPricing || this.bacsPricing;
    },
  },
  methods: {
    formatBasisPoints(basisPoints) {
      if (!basisPoints) return '0%';
      return `${(basisPoints / 100).toFixed(1)}%`;
    },
    formatCurrency(cents) {
      if (!cents) return '$0.00';
      return `$${(cents / 100).toFixed(2)}`;
    },
    formatPricing(pricing) {
      const percentage = this.formatBasisPoints(pricing.basis_points);
      const fixed = pricing.fixed_rate ? ` + ${this.formatCurrency(pricing.fixed_rate)}` : '';
      return `${percentage}${fixed}`;
    },
    formatMinMax(minFee, maxFee) {
      const parts = [];
      if (minFee) parts.push(`${this.formatCurrency(minFee)} minimum`);
      if (maxFee) parts.push(`${this.formatCurrency(maxFee)} maximum`);
      return parts.join(', ');
    },
  },
};
</script>

<style lang="scss">
.pricing-view-container {
  display: flex;
  gap: 24px;

  .pricing-content {
    flex: 1;
    max-width: 800px;
  }

  .pricing-sidebar {
    flex-shrink: 0;
    width: 200px;

    @media screen and (max-width: 1024px) {
      display: none;
    }
  }
}

.pricing-card {
  background: $color_white;
  border: 1px solid $neutral_200;
  border-radius: 8px;
  padding: 24px;
}

.pricing-list {
  text-align: left;

  .pricing-item {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    .pricing-label {
      font-weight: $weight_bold;
      font-size: 14px;
      line-height: 20px;
      color: $neutral_800;
      margin-bottom: 4px;
    }

    .pricing-value {
      font-size: 14px;
      line-height: 20px;
      color: $neutral_600;

      .pricing-amount {
        font-weight: $weight_bold;
      }

      .pricing-note {
        color: $neutral_400;
        font-size: 13px;
        font-weight: $weight_normal;
      }
    }
  }
}

.no-pricing {
  padding: 8px 0;
}
</style>
